import { lazy, Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ErrorBoundary from "../../../../shared/components/ErrorBoundary";
import { BoardState } from "../../domain/types";
import { boardBackdropStyle } from "../boardBackdrop";

// Lazy-load the heavy three.js bundle so it never weighs on the main bundle.
const ConstellationCanvas = lazy(() => import("./ConstellationCanvas"));

function isWebGLAvailable(): boolean {
    try {
        const canvas = document.createElement("canvas");
        return !!(
            window.WebGLRenderingContext &&
            (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
        );
    } catch {
        return false;
    }
}

type Props = {
    state: BoardState;
    loading?: boolean;
    selectedId?: string | null;
    onSelect?: (id: string | null) => void;
    onMove?: (id: string, x: number, z: number) => void;
    onRotate?: (id: string, delta: number) => void;
    onRemove?: (id: string) => void;
    onEdit?: (id: string) => void;
    onNoteMove?: (id: string, x: number, z: number) => void;
    onNoteEdit?: (id: string) => void;
    onNoteRemove?: (id: string) => void;
    onZoneMove?: (id: string, x: number, z: number) => void;
    onZoneEdit?: (id: string) => void;
    onZoneRemove?: (id: string) => void;
    onZoneResize?: (id: string, dims: { radius?: number; width?: number; depth?: number }) => void;
    onStartLink?: (id: string) => void;
    linkingFromId?: string | null;
    onLinkTarget?: (id: string) => void;
    onLinkEdit?: (id: string) => void;
    onLinkRemove?: (id: string) => void;
    readOnly?: boolean;
    // Figure ownership + immersion (issue #197). The immersive container and the
    // view-control buttons are owned by the page; the workspace only receives
    // the resolved view state.
    viewer?: { isTherapist: boolean; participantId: string | null };
    showOwners?: boolean;
    therapistLabel?: string;
    fitRequest?: number;
    focusFigureId?: string | null;
    zonesReadOnly?: boolean;
};

export default function ConstellationWorkspace({
    state,
    loading,
    selectedId,
    onSelect,
    onMove,
    onRotate,
    onRemove,
    onEdit,
    onNoteMove,
    onNoteEdit,
    onNoteRemove,
    onZoneMove,
    onZoneEdit,
    onZoneRemove,
    onZoneResize,
    onStartLink,
    linkingFromId,
    onLinkTarget,
    onLinkEdit,
    onLinkRemove,
    readOnly,
    viewer,
    showOwners,
    therapistLabel,
    fitRequest,
    focusFigureId,
    zonesReadOnly,
}: Props) {
    const { t } = useTranslation();
    const webglOk = useMemo(isWebGLAvailable, []);

    if (loading) {
        return (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
                {t("constellation.workspace.loading")}
            </div>
        );
    }

    if (!webglOk) {
        return (
            <div
                role="alert"
                className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-600"
            >
                {t("constellation.workspace.webglUnsupported")}
            </div>
        );
    }

    return (
        <ErrorBoundary
            fallback={
                <div role="alert" className="flex h-full items-center justify-center text-sm text-red-600">
                    {t("constellation.workspace.renderError")}
                </div>
            }
        >
            {/* The panorama illustration sits here, behind the transparent
                canvas, rather than as geometry in the scene. It is inside the
                snapshot capture target, so a capture keeps its backdrop. */}
            <div
                className="relative h-full w-full"
                style={boardBackdropStyle(state.ambiance)}
            >
                {state.puppets.length === 0 && (
                    <p className="pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2 text-xs text-gray-500">
                        {t("constellation.workspace.empty")}
                    </p>
                )}
                <Suspense
                    fallback={
                        <div className="flex h-full items-center justify-center text-sm text-gray-500">
                            {t("constellation.workspace.loading")}
                        </div>
                    }
                >
                    <ConstellationCanvas
                        state={state}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        onMove={onMove}
                        onRotate={onRotate}
                        onRemove={onRemove}
                        onEdit={onEdit}
                        onNoteMove={onNoteMove}
                        onNoteEdit={onNoteEdit}
                        onNoteRemove={onNoteRemove}
                        onZoneMove={onZoneMove}
                        onZoneEdit={onZoneEdit}
                        onZoneRemove={onZoneRemove}
                        onZoneResize={onZoneResize}
                        onStartLink={onStartLink}
                        linkingFromId={linkingFromId}
                        onLinkTarget={onLinkTarget}
                        onLinkEdit={onLinkEdit}
                        onLinkRemove={onLinkRemove}
                        readOnly={readOnly}
                        viewer={viewer}
                        showOwners={showOwners}
                        therapistLabel={therapistLabel}
                        fitRequest={fitRequest}
                        focusFigureId={focusFigureId}
                        zonesReadOnly={zonesReadOnly}
                    />
                </Suspense>
            </div>
        </ErrorBoundary>
    );
}
